import {
  authenticateWithAuthorizationCode,
  refreshAccessToken as cognitoRefreshAccessToken,
} from "api/endpoints/cognitoApi";
import { JWT_REFRESH_TOKEN_FIELD, JWT_TOKEN_FIELD } from "./constants";

// Constant for token refreshing status
const REFRESHING_TOKEN_STATUS_FIELD = "refreshing_access_token";

/**
 * Handles the Authorization Code authentication
 * @param code {string} - The OAuth authorization code used for authentication
 */
export const handleAuthorizationCodeAuthentication = async (code: string) => {
  const res = await authenticateWithAuthorizationCode(code);
  handleAuthorizationCodeAuthenticationResponse(res);
};

/**
 * Refreshes the OAuth access token
 * @returns {boolean} - Returns true if the token refresh is successful, false otherwise
 */
export const refreshAccessToken = async (): Promise<boolean> => {
  // Check if a token refresh is already in progress.
  // This is a bit dangerous, since if the user closes the page while the request is active,
  // They will be locked in this "currently refreshing" state.
  if (localStorage.getItem(REFRESHING_TOKEN_STATUS_FIELD)) {
    return true;
  }
  const refreshToken = localStorage.getItem(JWT_REFRESH_TOKEN_FIELD);
  if (!refreshToken) {
    console.error("No refresh token found");
    return false;
  }
  // Set the status to indicate token refresh is in progress
  localStorage.setItem(REFRESHING_TOKEN_STATUS_FIELD, "true");
  console.log("Refreshing access token");
  try {
    const res = await cognitoRefreshAccessToken(refreshToken);
    handleAuthorizationCodeAuthenticationResponse(res);
    return true;
  } catch (error) {
    console.error("Error refreshing access token", error);
    return false;
  } finally {
    // Remove the status indicating token refresh is complete
    localStorage.removeItem(REFRESHING_TOKEN_STATUS_FIELD);
  }
};

/**
 * Handles the response from the authorization code authentication
 * @param res {any} - The response object from the authentication request
 */
export const handleAuthorizationCodeAuthenticationResponse = (res: any) => {
  const { data } = res;
  const { access_token, refresh_token } = data;

  // Save the access token to local storage
  localStorage.setItem(JWT_TOKEN_FIELD, access_token);
  if (refresh_token) {
    // Save the refresh token to local storage if it exists
    localStorage.setItem(JWT_REFRESH_TOKEN_FIELD, refresh_token);
  }
  // Remove the token refreshing status
  localStorage.removeItem(REFRESHING_TOKEN_STATUS_FIELD);
};
